<template>
  <div class="booking-details-wrapper">
    <v-row
      class="booking-details"
      :class="{ 'base-footer-spacing': showBaseFooter }"
    >
      <v-col class="col-12">
        <Permissioned
          section="bookings"
          :id="currentBookingId"
          class="d-flex flex-column fill-height"
        >
          <Comment
            v-if="showComments || size(comments)"
            permission="view.comments"
            :comments="comments"
            :isAdding="isAddingComment"
            :is-loading="isLoadingComments"
            @comment="createNewComment"
            :minimizable="false"
            ref="comments"
            focusOnInit
            withAttachments
            :files="files"
            @unselectFile="unselectFile"
            @selectFile="selectFile"
          />
          <ApprovalProgress
            v-if="showApprovers"
            permission="view.approval-status"
            :approvers="currentBooking.bookingApprovers"
            @edit="isEditOpen = true"
          />
          <EditApprovers
            v-if="isEditOpen"
            :is-open="isEditOpen"
            :approvers="currentBooking.bookingApprovers"
            @close="isEditOpen = false"
          />
        </Permissioned>
      </v-col>
      <v-col class="col-12">
        <div class="mt-2" v-if="isLoadingBooking" data-test="loader">
          <div v-for="i in 3" :key="i">
            <v-skeleton-loader width="100%" type="card-heading" />
            <v-skeleton-loader
              v-for="i in 4"
              :key="i"
              width="100%"
              type="heading, list-item"
            />
          </div>
        </div>
        <Permissioned
          section="bookings"
          :id="currentBookingId"
          vertical
          v-else
          data-test="bookingDetails"
        >
          <BasicDetails />
          <BookingApplicationQuestions
            :loading="isFetchingBookingOptions"
            :bookingOptions="bookingOptions"
            v-if="!isNestedInApplication && bookingOptions"
          />
          <InternalInformation />
          <PublishingSchedule
            v-if="!isCandidate"
            class="mb-4 pb-4 base-border-bottom"
            permission="view.publishing-schedule"
            :bookingId="currentBookingId"
            :bookingStatus="status"
          />
        </Permissioned>
      </v-col>
    </v-row>
    <BaseFooter v-if="showBaseFooter && !isLoadingBooking">
      <ExpandedButtons>
        <Permissioned
          class="d-flex actions-bar"
          :vertical="isMobile"
          section="bookings"
          :id="currentBookingId"
        >
          <DeleteBookingAction permission="delete" />
          <CancelBookingAction permission="cancel" />
          <SubmitForApprovalAction permission="submit-for-approval" />
          <RejectBookingAction permission="reject" @reject="handleReject" />
          <ApproveBookingAction permission="approve" />
          <AcceptBookingAction permission="accept" />
          <CloseBookingAction permission="close" />
          <ReopenBookingAction permission="reopen" />
          <SubmitWorkerAction permission="submit-worker" />
          <ShowCommentsButton
            permission="view.comments"
            v-if="!size(comments)"
            @click.native="openCommentsSection"
          />
        </Permissioned>
      </ExpandedButtons>
    </BaseFooter>
  </div>
</template>
<script>
import BasicDetails from "@/views/bookings/BookingDetails/components/BasicDetails";
import InternalInformation from "@/views/bookings/BookingDetails/components/InternalInformation";
import BookingApplicationQuestions from "@/views/bookings/BookingDetails/components/BookingApplicationQuestions";
import PublishingSchedule from "@/views/bookings/BookingDetails/components/PublishingSchedule";
import Comment from "@/components/common/Comment";
import BaseFooter from "@/components/common/BaseFooter";
import Permissioned from "@/components/common/Permissioned";
import ExpandedButtons from "@/components/common/ExpandedButtons";
import DeleteBookingAction from "@/views/bookings/components/actions/DeleteBookingAction";
import SubmitForApprovalAction from "@/views/bookings/components/actions/SubmitForApprovalAction";
import RejectBookingAction from "@/views/bookings/components/actions/RejectBookingAction";
import ApproveBookingAction from "@/views/bookings/components/actions/ApproveBookingAction";
import AcceptBookingAction from "@/views/bookings/components/actions/AcceptBookingAction";
import SubmitWorkerAction from "@/views/bookings/components/actions/SubmitWorkerAction";
import CancelBookingAction from "@/views/bookings/components/actions/CancelBookingAction";
import CloseBookingAction from "@/views/bookings/components/actions/CloseBookingAction";
import ReopenBookingAction from "@/views/bookings/components/actions/ReopenBookingAction";
import ShowCommentsButton from "@/components/common/Button/ShowCommentsButton";
import ApprovalProgress from "@/components/bookings/ApprovalProgress";
import { remove, size, map, get } from "lodash";
import { scrollToPageTop } from "@/utils/helpers";
import {
  ADD_COMMENT,
  BOOKINGS_NAMESPACE,
  FETCH_CURRENT_BOOKING_COMMENTS,
  FETCH_CURRENT_BOOKING,
  FETCH_BOOKING_OPTIONS
} from "@/store/modules/bookings/actions";
import {
  GET_CURRENT_BOOKING_COMMENTS,
  IS_LOADING_BOOKING_COMMENTS,
  IS_LOADING_BOOKING,
  GET_BOOKING_OPTIONS,
  IS_FETCHING_BOOKING_OPTIONS
} from "@/store/modules/bookings/getters";
import { createNamespacedHelpers, mapState } from "vuex";
import { BOOKING_MODEL } from "@/models/booking-model";
import { ADD_COMMENT_FILE } from "@/store/modules/data/actions";
import { DATA_NAMESPACE } from "@/store/modules/data";
import EditApprovers from "@/views/bookings/EditBooking/EditApprovers";

const {
  mapActions: mapBookingActions,
  mapGetters: mapBookingGetters
} = createNamespacedHelpers(BOOKINGS_NAMESPACE);
const { mapActions: mapDataActions } = createNamespacedHelpers(DATA_NAMESPACE);

export default {
  name: "BookingDetails",
  components: {
    CancelBookingAction,
    SubmitWorkerAction,
    AcceptBookingAction,
    ApproveBookingAction,
    RejectBookingAction,
    SubmitForApprovalAction,
    DeleteBookingAction,
    Permissioned,
    BaseFooter,
    Comment,
    PublishingSchedule,
    InternalInformation,
    BasicDetails,
    ShowCommentsButton,
    ApprovalProgress,
    BookingApplicationQuestions,
    CloseBookingAction,
    ExpandedButtons,
    ReopenBookingAction,
    EditApprovers
  },
  props: {
    currentBooking: {
      type: Object,
      required: true
    },
    isNestedInApplication: Boolean,
    isCandidate: Boolean
  },
  data() {
    return {
      showComments: false,
      files: [],
      isAddingComment: false,
      isEditOpen: false
    };
  },
  created() {
    if (!this.currentBooking) {
      this.fetchCurrentBooking(this.currentBookingId);
    }
    if (this.shouldFetchComments) {
      this.fetchCurrentBookingComments(this.currentBookingId);
    }
    this.fetchBookingOptions(this.currentBookingId);
  },
  computed: {
    ...mapState("auth", {
      permissions: state => state.permissions
    }),
    currentBookingId() {
      return this.isNestedInApplication
        ? this.currentBooking.id
        : this.$route.params.id;
    },
    ...mapBookingGetters({
      comments: GET_CURRENT_BOOKING_COMMENTS,
      isLoadingBookingComments: IS_LOADING_BOOKING_COMMENTS,
      isLoadingBooking: IS_LOADING_BOOKING,
      bookingOptions: GET_BOOKING_OPTIONS,
      isFetchingBookingOptions: IS_FETCHING_BOOKING_OPTIONS
    }),
    isLoadingComments() {
      return this.isLoadingBookingComments && !this.comments.length;
    },
    showBaseFooter() {
      return this.isSomeActionPermitted && !this.isNestedInApplication;
    },
    isSomeActionPermitted() {
      return (
        this.permissions["bookings.delete"] ||
        this.permissions["bookings.cancel"] ||
        this.permissions["bookings.submit-for-approval"] ||
        this.permissions["bookings.reject"] ||
        this.permissions["bookings.approve"] ||
        this.permissions["bookings.accept"] ||
        this.permissions["bookings.submit-worker"]
      );
    },
    shouldFetchComments() {
      return this.permissions["bookings.view.comments"] && !this.isCandidate;
    },
    status() {
      return get(this.currentBooking, "status");
    },
    showApprovers() {
      return this.status !== "draft";
    }
  },
  methods: {
    ...mapBookingActions({
      fetchCurrentBookingComments: FETCH_CURRENT_BOOKING_COMMENTS,
      addComment: ADD_COMMENT,
      fetchCurrentBooking: FETCH_CURRENT_BOOKING,
      fetchBookingOptions: FETCH_BOOKING_OPTIONS
    }),
    ...mapDataActions({
      addCommentFile: ADD_COMMENT_FILE
    }),
    async createNewComment(comment) {
      this.isAddingComment = true;
      const { data: newComment } = await this.addComment({
        bookingId: this.currentBookingId,
        comment: {
          body: comment,
          commentableType: BOOKING_MODEL,
          commentableId: this.currentBookingId
        }
      });
      if (newComment) {
        await this.uploadCommentFiles(newComment.id);
      }
      this.isAddingComment = false;
      await this.fetchCurrentBookingComments(this.currentBookingId);
    },
    async uploadCommentFiles(commentId) {
      const requests = map(this.files, file => {
        const formData = new FormData();
        formData.append("file", file.data);
        return this.addCommentFile({
          id: commentId,
          data: formData
        });
      });
      this.files = [];
      this.$refs.comments.resetFileInput();
      return Promise.all(requests);
    },
    async handleReject() {
      await this.fetchCurrentBookingComments(this.currentBookingId);
    },
    openCommentsSection() {
      this.showComments = true;
      scrollToPageTop();
      if (this.$refs.comments) {
        this.$refs.comments.focusOnTextField();
      }
    },
    selectFile(file) {
      this.files.push(file);
    },
    unselectFile(fileToBeRemoved) {
      remove(this.files, file => file.id === fileToBeRemoved.id);
    },
    size
  }
};
</script>

<style lang="scss"></style>
